import {Placement} from '@popperjs/core';
import {noop} from 'lodash';
import React, {FC, PropsWithChildren, useCallback, useEffect} from 'react';
import styled from 'styled-components';

import {globalWindow} from '../../../../libs/shared/core/src/helpers/browser/browserHelpers';
import {TaskSourcesEnum} from '../../../../libs/shared/core/src/helpers/instrumentation/eventLoopPerformanceHelpers';
import {InteractionTypesEnum} from '../../../../libs/shared/core/src/helpers/interaction/interactionTypes';
import {useEventHandler} from '../../../../libs/shared/core/src/helpers/react/hooks/useEventHandler';
import {Layer} from '../../../../libs/shared/ui/src/components/layers/layer';
import {Anchor} from '../../../../libs/shared/ui/src/components/reposition/repositionHelpers';
import {TipReposition} from '../../../../libs/shared/ui/src/components/tip/tipReposition';
import {useLegacyInteractionContext} from '../../../../libs/shared/ui/src/interactionTracking/interactionContext';
import {useInteractionTracking} from '../../../../libs/shared/ui/src/interactionTracking/interactionTrackingContext';
import {infoBubbleAnimationStyle} from './infoBubbleCommon';
import {HighlightPulseStylesEnum, InfoBubbleHighlight} from './infoBubbleHighlight';
import {computeInfoBubbleOverlayDefaultOffset, InfoBubbleOverlay} from './infoBubbleOverlay';

/*
 * Props.
 */

export interface InfoBubbleInstanceProps {
  /** Ref to the target element. */
  anchor: Anchor;
}

export interface InfoBubbleProps extends InfoBubbleInstanceProps {
  className?: string;

  /** The position of the bubble relative to its target. */
  position: Placement;
  /** Whether the info bubble is visible on screen. */
  isVisible: boolean;
  /** Whether to allow pointer events on the target. */
  isInteractive?: boolean;
  /** Whether the bubble should appear without an overlay. */
  isStandalone?: boolean;
  /** The style of the highlight pulse effect. No highlight if undefined. */
  highlightPulseStyle?: HighlightPulseStylesEnum;
  /** Handler to update state when info bubble is closed. */
  onRequestClose: () => void;
  /** Optional handler for clicks outside of the highlighted target. If provided, will prevent default click action. */
  onOutsideHighlightClick?: () => void;

  interactionId?: string;
  /** Customizes the space around the overlay highlight. */
  overlayOffset?: number;
  /** Customizes border radius of the overlay highlight. */
  overlayBorderRadius?: number;
  scrollsIntoView?: boolean;
}

/*
 * Style.
 */

// Note: the blur radius for drop-shadow() is about half of what you'd use for box-shadow!
const StyledTipReposition = styled(TipReposition)`
  ${infoBubbleAnimationStyle};

  filter: drop-shadow(0 2px 5px rgba(0, 0, 0, 0.2));
`;

const StyledInfoBubbleDiv = styled.div`
  width: 410px;
`;

/*
 * Component.
 */

export const InfoBubble: FC<PropsWithChildren<InfoBubbleProps>> = (props) => {
  const {
    anchor,
    position,
    isVisible,
    isInteractive,
    isStandalone,
    highlightPulseStyle,
    onRequestClose,
    onOutsideHighlightClick,
    children,
    interactionId,
    scrollsIntoView = true,
  } = props;

  const isVisibleWithChildren = isVisible && Boolean(children);

  const onScrollIntoView = useEventHandler(() => {
    // TODO: Scroll only if the target is not actually visible.
    if ('current' in anchor && scrollsIntoView) {
      anchor.current?.scrollIntoView();
    }
  });

  const {trackInfoBubbleShown} = useInfoBubbleInteractionTracking();
  const onBecameVisibleWithChildren = useEventHandler(() => {
    trackInfoBubbleShown(interactionId);
  });

  useEffect(() => {
    if (!isVisibleWithChildren) {
      return noop;
    }

    // Scroll the target into view to avoid being stuck.
    const handle = globalWindow.requestInstrumentedAnimationFrame(onScrollIntoView, {
      source: TaskSourcesEnum.COMPONENT_RENDER,
    });

    onBecameVisibleWithChildren();

    return () => {
      globalWindow.cancelAnimationFrame(handle);
    };
  }, [isVisibleWithChildren, onScrollIntoView, onBecameVisibleWithChildren]);

  const hasPopover = Boolean(children);
  const overlayOffset = props.overlayOffset ?? computeInfoBubbleOverlayDefaultOffset(hasPopover);
  const offset = isStandalone ? 0 : overlayOffset;

  // Only render the info bubble if we've been given some bubble content to render via children.
  // This allows us to render only the overlay on the target when necessary.
  const maybeRenderBubble = () => {
    if (!children) {
      return null;
    }

    return (
      <Layer isInteractive={isInteractive}>
        <StyledTipReposition
          anchor={anchor}
          placement={position}
          isArrowVisible={Boolean(isStandalone)}
          offset={offset}
        >
          <StyledInfoBubbleDiv className={props.className}>{children}</StyledInfoBubbleDiv>
        </StyledTipReposition>
      </Layer>
    );
  };

  // If the bubble should appear standalone, do not render the overlay.
  const maybeRenderOverlay = () => {
    if (isStandalone) {
      return null;
    }

    return (
      <InfoBubbleOverlay
        anchor={anchor}
        hasPopover={hasPopover}
        isInteractive={isInteractive}
        onClick={() => {
          onRequestClose();
        }}
        offset={overlayOffset}
        borderRadius={props.overlayBorderRadius}
      />
    );
  };

  // Render the target with an optional highlight pulse effect.
  const maybeRenderHighlight = () => {
    if (!highlightPulseStyle) {
      return null;
    }

    return (
      <InfoBubbleHighlight
        anchor={anchor}
        highlightPulseStyle={highlightPulseStyle}
        onOutsideHighlightClick={onOutsideHighlightClick}
      />
    );
  };

  if (!isVisible) {
    return null;
  }

  return (
    <>
      {maybeRenderOverlay()}
      {maybeRenderBubble()}
      {maybeRenderHighlight()}
    </>
  );
};

/*
 * Custom hooks.
 */

function useInfoBubbleInteractionTracking() {
  const legacyInteractionContext = useLegacyInteractionContext();
  const {parentInteractionIds} = legacyInteractionContext;
  const {trackInteraction} = useInteractionTracking();

  const trackInfoBubbleShown = useCallback(
    (interactionId: string | undefined) => {
      // Legacy interaction tracking
      trackInteraction(
        InteractionTypesEnum.INFOBUBBLE_SHOWN,
        interactionId || 'infoBubble',
        parentInteractionIds,
      );
    },
    [parentInteractionIds, trackInteraction],
  );

  return {trackInfoBubbleShown};
}
